/**
 * ForgeSoundModal — one place to see and change the sound settings a Parliament Polish run uses.
 *
 * Polish already reads the app-wide Vocal / Instrument / Solo / Mix / Genre state when it runs; this
 * only makes those choices visible and editable next to the lyric box. It edits the SAME state as the
 * Vocals, Production and Solo tabs, so a change here shows up there (and the other way round).
 */

import React, { useState } from 'react';
import { Mic2, Guitar, Zap, SlidersHorizontal, Music2, ChevronRight, X } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { QUICK_BLEND_GROUPS } from '@/types';
import { useVocalConfig } from '@context/VocalConfigContext';
import { useProductionConfig } from '@context/ProductionConfigContext';
import { InstrumentForgeProvider, InstrumentForgeSelector } from '@features/InstrumentForge';
import { useSoloChoreographer } from '@features/SoloChoreographer';
import SoloTemplateBrowser from '@features/SoloChoreographer/components/SoloTemplateBrowser';
import MixTemplateBrowser from '@components/ProductionIntelligence/MixTemplateBrowser';
import { VocalPackSection } from '@components/VocalForge/VocalPackSection';
import { useSoloTemplates } from '@hooks/useSoloTemplates';
import { useInstrumentProfiles } from '@hooks/useInstrumentProfiles';

export interface ForgeChip {
  key: 'vocal' | 'instrument' | 'solo' | 'mix' | 'genre';
  label: string;
  /** What is currently selected, or null when nothing is. */
  value: string | null;
}

/** What is active right now, for the chips on the Polish title bar. */
export function useForgeSummary(activeBlends: string[]): ForgeChip[] {
  const { config: vocal } = useVocalConfig();
  const { state: solo } = useSoloChoreographer();
  // The APPLIED instrument choice lives in the production config. InstrumentForgeProvider is mounted only
  // inside the Production tab, so reading its context from here would throw and take the whole app down.
  const { config: production, instrumentForgeConfig } = useProductionConfig();
  const { data: soloTemplates } = useSoloTemplates();
  const { data: instrumentProfiles } = useInstrumentProfiles();

  const vocalValue = vocal.mode === 'blend' ? 'Blend' : vocal.singlePackKey || null;
  const instrumentValue = !instrumentForgeConfig?.profileAKey
    ? null
    : instrumentForgeConfig.profileBKey
      ? 'Blend'
      : instrumentProfiles[instrumentForgeConfig.profileAKey]?.name ?? instrumentForgeConfig.profileAKey;
  const soloValue = solo.activeTemplateId
    ? soloTemplates.find(t => t.id === solo.activeTemplateId)?.name ?? 'Custom'
    : null;

  return [
    { key: 'genre', label: 'Genre', value: activeBlends.length ? activeBlends.join(' + ') : null },
    { key: 'vocal', label: 'Vocal', value: vocalValue },
    { key: 'instrument', label: 'Instruments', value: instrumentValue },
    { key: 'solo', label: 'Solo', value: soloValue },
    { key: 'mix', label: 'Mix', value: production.mixTemplate?.name ?? null },
  ];
}

const MAX_BLENDS = 3;

const Section: React.FC<{ icon: React.ReactNode; title: string; hint?: string; children: React.ReactNode }> = ({ icon, title, hint, children }) => (
  <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-4 space-y-3">
    <header className="flex items-center gap-2">
      <span className="text-accent">{icon}</span>
      <h3 className="text-[11px] font-black text-slate-300 uppercase tracking-widest">{title}</h3>
      {hint && <span className="text-[10px] text-slate-500 ml-auto">{hint}</span>}
    </header>
    {children}
  </section>
);

const PickRow: React.FC<{ value: string | null; onChoose: () => void; onClear?: () => void }> = ({ value, onChoose, onClear }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={onChoose}
      className="group flex-1 flex items-center justify-between rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-left hover:border-accent/60 transition-colors"
    >
      <span className={`text-sm ${value ? 'text-slate-100' : 'text-slate-500'}`}>{value ?? 'None selected — choose…'}</span>
      <ChevronRight size={14} className="text-slate-500 group-hover:text-accent transition-colors" />
    </button>
    {value && onClear && (
      <button type="button" onClick={onClear} aria-label="Clear" className="p-2 text-slate-500 hover:text-red-400 transition-colors">
        <X size={14} />
      </button>
    )}
  </div>
);

interface ForgeSoundModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeBlends: string[];
  onBlendChange?: (blends: string[]) => void;
}

export const ForgeSoundModal: React.FC<ForgeSoundModalProps> = ({ isOpen, onClose, activeBlends, onBlendChange }) => {
  const chips = useForgeSummary(activeBlends);
  const { config: production, applyProductionProfile, clearInstrumentForge } = useProductionConfig();
  const { state: solo, applyTemplate, clearSolo } = useSoloChoreographer();
  const [soloOpen, setSoloOpen] = useState(false);
  const [mixOpen, setMixOpen] = useState(false);
  // The picker brings its own provider and applies its stored choice the moment it mounts, so it only
  // mounts when asked for; merely opening this modal must not change the song's instrument layers.
  const [instrumentsOpen, setInstrumentsOpen] = useState(false);

  const valueOf = (key: ForgeChip['key']) => chips.find(c => c.key === key)?.value ?? null;

  const toggleBlend = (label: string) => {
    if (!onBlendChange) return;
    if (activeBlends.includes(label)) onBlendChange(activeBlends.filter(b => b !== label));
    else if (activeBlends.length < MAX_BLENDS) onBlendChange([...activeBlends, label]);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Sound for this Polish"
        size="xl"
        // Escape belongs to the Solo/Mix browser while one is open on top; otherwise it would close both.
        closeOnEscape={!soloOpen && !mixOpen}
        footer={
          <div className="flex items-center justify-between gap-3 w-full">
            <p className="text-[11px] text-slate-500">
              These are the same settings as the Vocals, Production, Instrument and Solo tabs — a change here changes them there.
            </p>
            <Button type="button" onClick={onClose}>Done</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Section icon={<Music2 size={16} />} title="Genre" hint={`up to ${MAX_BLENDS}`}>
            <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
              {QUICK_BLEND_GROUPS.map(group => (
                <div key={group.name}>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">{group.name}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {group.options.map(o => {
                      const on = activeBlends.includes(o.label);
                      const full = !on && activeBlends.length >= MAX_BLENDS;
                      return (
                        <button
                          key={o.label}
                          type="button"
                          title={o.description}
                          disabled={full}
                          onClick={() => toggleBlend(o.label)}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                            on ? 'bg-accent text-white border-accent'
                              : full ? 'border-slate-800 text-slate-600 cursor-not-allowed'
                                : 'border-slate-700 text-slate-300 hover:border-accent/60'
                          }`}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={<Mic2 size={16} />} title="Vocals">
            <VocalPackSection />
          </Section>

          <Section icon={<Guitar size={16} />} title="Instruments">
            {instrumentsOpen ? (
              <div className="space-y-2">
                <InstrumentForgeProvider>
                  <InstrumentForgeSelector />
                </InstrumentForgeProvider>
                <button type="button" onClick={() => setInstrumentsOpen(false)} className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors">
                  Hide instrument picker
                </button>
              </div>
            ) : (
              <PickRow
                value={valueOf('instrument')}
                onChoose={() => setInstrumentsOpen(true)}
                onClear={valueOf('instrument') ? clearInstrumentForge : undefined}
              />
            )}
          </Section>

          <div className="grid gap-4 md:grid-cols-2">
            <Section icon={<Zap size={16} />} title="Solo">
              <PickRow value={valueOf('solo')} onChoose={() => setSoloOpen(true)} onClear={solo.activeTemplateId ? clearSolo : undefined} />
            </Section>
            <Section icon={<SlidersHorizontal size={16} />} title="Mix">
              <PickRow
                value={valueOf('mix')}
                onChoose={() => setMixOpen(true)}
                onClear={production.mixTemplate ? () => applyProductionProfile({ ...production, mixTemplate: null }) : undefined}
              />
            </Section>
          </div>
        </div>
      </Modal>

      <SoloTemplateBrowser
        isOpen={soloOpen}
        onClose={() => setSoloOpen(false)}
        onSelect={id => { applyTemplate(id); setSoloOpen(false); }}
        selectedId={solo.activeTemplateId}
      />
      <MixTemplateBrowser
        isOpen={mixOpen}
        onClose={() => setMixOpen(false)}
        onSelect={template => { applyProductionProfile({ ...production, mixTemplate: template }); setMixOpen(false); }}
        selectedId={production.mixTemplate?.id}
      />
    </>
  );
};
